import React from "react"
import { useTranslation } from "react-i18next"
import FormElement from "components/Form/FormElement"
import { isVATNumberValid, isRnaValid, isSimpleDateFormat } from "utils/validation"
import dateService from "services/date"
import { defaultEntity as defaultOperator, operators } from "utils/constants/operators"
import { useAuth } from "hooks/useAuth"
import { jsonHash } from "utils/hash"
import {
	AM_GENDER,
	COLLECTIVITE_GENDER,
	HORS_AM_GENDER,
	OCODE_GENDERS_LIST,
	VELOCISTE_GENDER,
	apeAssociationList,
	apeCompanyList,
	entityGenderList,
	genderStoreList,
	partnerChainList,
} from "utils/constants"
import { LocalEntity } from "models"
import { FormError } from "models/form"

type StructureFormProps = {
	partner: LocalEntity
	setUpdatedPartner: React.Dispatch<React.SetStateAction<Partial<LocalEntity>>>
	updatedPartner: LocalEntity
	errors: Array<FormError>
	setErrors: React.Dispatch<React.SetStateAction<Array<FormError>>>
	isEdit: boolean
	isViewMode: boolean
	editCallback: React.Dispatch<React.SetStateAction<Partial<LocalEntity>>>
	showLocalErrors: boolean
	setShowLocalErrors: React.Dispatch<React.SetStateAction<boolean>>
}

const FRENCH_CODE = ["FR"] // Quand MAJ de Salesforce => ["FR", "RE", "YT", "GP", "MQ", "YT"] // Inclunding 5 DOM : Guadeloupe, Martinique, Guyane, Réunion and Mayotte

export const StructureForm = ({
	partner,
	editCallback,
	updatedPartner,
	setUpdatedPartner,
	errors,
	setErrors,
	isEdit,
	isViewMode,
	showLocalErrors,
	setShowLocalErrors,
}: StructureFormProps) => {
	const defaultFormFields: Partial<LocalEntity> = React.useMemo(
		() => ({
			gender: 0,
			isFub: false,
			raison_sociale: "",
			code_ape: "",
			nom_commercial: "",
			isFranchise: false,
			structureChainName: "",
			isMaker: false,
			rna: "",
			registre_date: new Date(),
			registre_volume: "",
			registre_folio: "",
			tva_intra: "",
			siret: "",
		}),
		[]
	)

	const [formFields, setFormFields] = React.useState(defaultFormFields)
	const defaultFormEvents = React.useMemo(
		() => ({
			ready: true,
			// when form is in edition mode, by default the editable is set at false
			// when the form is not in edition mode, it means creation mode, the form should be editable
			editable: !isEdit,
			viewMode: isViewMode || false,
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}),
		[isEdit, isViewMode]
	)
	const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
	const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

	const isFrenchCountrySelected = updatedPartner?.pays
		? FRENCH_CODE.includes(updatedPartner?.pays)
		: FRENCH_CODE.includes(partner?.pays)

	const { t } = useTranslation()
	const { user, entity } = useAuth()

	React.useEffect(
		() => {
			// When partner reloaded (after await fetchData()), set data in formFields
			if (partner) setFormFields(partner)
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[partner]
	)

	React.useEffect(
		() => {
			// When we create a new partner, we have to pass data to parent directly.
			// We don't have any submit button on this component in this case
			if (!isEdit)
				setUpdatedPartner((prevFormFields) => ({
					...prevFormFields,
					gender: formFields.gender,
					isFub: formFields.isFub,
					raison_sociale: formFields.raison_sociale.trim(),
					code_ape: formFields.code_ape,
					nom_commercial: formFields.nom_commercial.trim(),
					isFranchise: formFields.isFranchise,
					structureChainName: formFields.structureChainName,
					isMaker: formFields.isMaker,
					rna: formFields.rna,
					registre_date: formFields.registre_date,
					registre_volume: formFields.registre_volume,
					registre_folio: formFields.registre_folio,
					tva_intra: formFields.tva_intra,
					siret: formFields.siret,
				}))
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isEdit, formFields]
	)

	const entityRole = entity?.entity_data?.entity_permission?.role
	const isAdminOCode = OCODE_GENDERS_LIST.includes(user?.gender)

	const handleInputChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, type: string, fieldName) => {
			const target = event.target
			let value: string | boolean | React.ChangeEvent<HTMLInputElement>
			let name: string

			if (!target && (type = "date")) {
				name = fieldName
				value = event
			} else {
				value = target.type === "checkbox" ? target.checked : target.value
				name = target.name
			}

			setFormFields((s) => ({
				...s,
				[name]: value,
			}))
		},
		[]
	)

	const toggleEditableForm = (event) => {
		event.preventDefault()
		setFormEvents({
			...formEvents,
			editable: !formEvents.editable,
		})
	}

	const handleValidation = React.useCallback((name: string, errs: FormError[]) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const handleSubmit = async (event: React.SyntheticEvent) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			// hide error
			setShowLocalErrors(false)
			// prevent form to be submitted again
			setFormEvents((s) => ({
				...s,
				ready: false,
			}))

			const siret = (() => {
				switch (formFields.gender) {
					case 1: // hors Alsace Moselle
						return "WWWWW" + formFields.rna
					case 2: // Alsace Moselle
						// We test the date format. Then, we format the new date to send it
						const currentRegisterDate = isSimpleDateFormat(formFields.registre_date)
							? new Date(formFields.registre_date)
							: formFields.registre_date

						const registerDate = dateService.formatDate(currentRegisterDate, "dd/MM/y")

						return (
							registerDate.replace(/\//g, "") +
							formFields.registre_volume +
							formFields.registre_folio
						)
					default: // Velocistes / Collectivites
						return formFields.siret || ""
				}
			})()

			const updatedPartner = {
				...formFields,
				siret,
				gender: formFields.gender,
				isFub: formFields.isFub,
				raison_sociale: formFields.raison_sociale,
				code_ape: formFields.code_ape,
				nom_commercial: formFields.nom_commercial,
				isFranchise: formFields.isFranchise,
				structureChainName: formFields.structureChainName,
				isMaker: formFields.isMaker,
				rna: formFields.rna,
				registre_date: formFields.registre_date,
				registre_volume: formFields.registre_volume,
				registre_folio: formFields.registre_folio,
				tva_intra: formFields.tva_intra,
			}

			editCallback(updatedPartner)

			setFormEvents((s) => ({
				...s,
				ready: true,
				editable: false,
			}))
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	return (
		<form className="">
			<section className="c-card p-5 md:p-12 mb-7">
				<h2 className="c-h2 font-semibold mb-7 uppercase">
					{t("store.structure", { ns: "partner" })}
				</h2>
				<div className="l-grid u-mg-negative-hz-m">
					<div className="lg:w-1/2 w-full p-5">
						{/* Type de structure */}

						{isEdit || entityRole === "MANAGEBIKES" ? (
							<div className="c-form-group mb-5">
								<label className="c-label mb-5">
									{t("type", { ns: "partner" })} :{" "}
								</label>
								<div className="flex items-baseline text-xs">
									{t(
										`${genderStoreList.find(
											(g) => g.value === formFields.gender
										)?.label}`
									)}
								</div>
							</div>
						) : (
							<FormElement
								{...formElementCommonProps}
								value={formFields.gender}
								name="gender"
								type="radio"
								options={genderStoreList.filter((g) => {
									const userOperatorId = entityGenderList.find(
										(e) => e.value === user.gender
									)?.operator_id
									const correspondingOperator = [
										defaultOperator,
										...operators,
									].find((o) => o.id === userOperatorId)
									return correspondingOperator?.allowed_gender.includes(g.value)
								})}
								classNameOptionsContainer="flex-col"
								classNameLabel="mb-5"
								classNameOption="flex-1 items-center"
								label={t("type", { ns: "partner" })}
								required={true}
							/>
						)}
					</div>
					<div className="lg:w-1/2 w-full p-5">
						{/* FUB member */}
						{(formFields.gender === HORS_AM_GENDER ||
							formFields.gender === AM_GENDER) && ( // Blocks appear if association selected, gender 1 or 2
							<FormElement
								{...formElementCommonProps}
								value={formFields.isFub}
								name="isFub"
								type="radio"
								label={t("edit.afub-member", { ns: "partner" })}
								options={[
									{ id: "afub-true", value: "true", label: "global.yes" },
									{ id: "afub-false", value: "false", label: "global.no" },
								]}
								classNameOptionsContainer="flex-row w-full"
								classNameLabel="mb-5"
								classNameOption="flex-1 items-center"
								required={true}
								readonly={!formEvents.editable}
							/>
						)}
					</div>
					<div className="lg:w-1/2 w-full p-5">
						{/* raison_sociale */}
						<FormElement
							{...formElementCommonProps}
							value={formFields.raison_sociale}
							name="raison_sociale"
							type="text"
							label={t("store.structure_name", { ns: "partner" })}
							className="mb-5"
							required={true}
							readonly={!formEvents.editable}
						/>
					</div>
					<div className="lg:w-1/2 w-full p-5">
						{/* code_ape */}
						<FormElement
							{...formElementCommonProps}
							value={formFields.code_ape}
							defaultValue={{
								label:
									(formFields.gender === VELOCISTE_GENDER
										? apeCompanyList
										: apeAssociationList
									).find((b) => b.value === formFields.code_ape)?.label ||
									formFields.code_ape,
								value: formFields.code_ape,
							}}
							options={
								formFields.gender === VELOCISTE_GENDER
									? apeCompanyList
									: apeAssociationList
							}
							name="code_ape"
							id="code_ape"
							type="select"
							label={t("store.structure_code_ape", { ns: "partner" })}
							className="mb-5"
							required={true}
							readonly={!formEvents.editable}
						/>
					</div>

					<div className="lg:w-1/2 w-full p-5">
						{/* nom_commercial */}
						<FormElement
							{...formElementCommonProps}
							value={formFields.nom_commercial}
							name="nom_commercial"
							type="text"
							label={t("store.structure_commercial_name", { ns: "partner" })}
							className="mb-5"
							required={true}
							readonly={!formEvents.editable}
						/>
					</div>

					{(formFields.gender === VELOCISTE_GENDER ||
						formFields.gender === COLLECTIVITE_GENDER) && ( // Blocks appear if velociste or collectivite selected, gender 0 or 3
						<>
							<div className="lg:w-1/2 w-full p-5">
								{/* siret */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.siret}
									name="siret"
									type="text"
									maxLength={14}
									label={t("store.structure_siret", { ns: "partner" })}
									className="mb-5"
									required={isFrenchCountrySelected}
									/*customRules={[
                                        {
                                            key: "siret-invalid",
                                            check: (value) => isSiretValid(value),
                                            message: "Le numéro SIRET n'est pas valide."
                                        }
                                    ]}*/
									disabled={
										(!isAdminOCode && !formEvents.editable) ||
										(!isAdminOCode && isEdit)
									} // Siret available for all when we create new partner
									readonly={!formEvents.editable}
								/>
							</div>
							<div className="lg:w-1/2 w-full p-5">
								{/* VAT N° */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.tva_intra}
									name="tva_intra"
									type="text"
									maxLength={14}
									label={t("store.vatNumber", { ns: "partner" })}
									className="mb-5"
									required={!isFrenchCountrySelected}
									customRules={[
										{
											key: "vat-invalid",
											check: (value) => {
												return (
													(!isFrenchCountrySelected &&
														isVATNumberValid(value)) ||
													true
												)
											},
											message: t("store.invalidVatNumber", { ns: "partner" }),
										},
									]}
									disabled={
										(!isAdminOCode && !formEvents.editable) ||
										(!isAdminOCode && isEdit)
									}
									readonly={!formEvents.editable}
								/>
							</div>
							<div className="lg:w-1/2 w-full"></div>
						</>
					)}

					{formFields.gender === VELOCISTE_GENDER && ( // Blocks appear if velociste selected, gender 0
						<>
							<div className="lg:w-1/2 w-full p-5">
								{/* Chaine / Franchise */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.isFranchise}
									name="isFranchise"
									type="radio"
									label={t("isFranchise", { ns: "partner" })}
									options={[
										{
											id: "franchise-true",
											value: "true",
											label: "global.yes",
										},
										{
											id: "franchise-false",
											value: "false",
											label: "global.no",
										},
									]}
									className="mb-7"
									classNameOptionsContainer="flex-row w-full"
									classNameLabel="mb-5"
									classNameOption="flex-1 items-center"
									readonly={!formEvents.editable}
									disabled={false}
								/>
							</div>

							<div className="lg:w-1/2 w-full p-5">
								{/* structureChainName */}
								{formFields.isFranchise && (
									<FormElement
										{...formElementCommonProps}
										value={formFields.structureChainName}
										defaultValue={{
											label:
												partnerChainList.find(
													(chain) =>
														chain.value ===
														formFields.structureChainName
												)?.label || "",
											value: formFields.structureChainName,
										}}
										options={partnerChainList}
										name="structureChainName"
										id="structureChainName"
										type="select"
										label={t("store.structure_chain_name", { ns: "partner" })}
										className="mb-5"
										required={false}
										readonly={!formEvents.editable}
										disabled={false}
									/>
								)}
							</div>

							<div className="lg:w-1/2 w-full p-5">
								{/* Fabricant / Marque  */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.isMaker}
									name="isMaker"
									type="radio"
									label={t("isMaker", { ns: "partner" })}
									options={[
										{ id: "maker-true", value: "true", label: "global.yes" },
										{ id: "maker-false", value: "false", label: "global.no" },
									]}
									className=""
									classNameOptionsContainer="flex-row w-full"
									classNameLabel="mb-5"
									classNameOption="flex-1 items-center"
									readonly={!formEvents.editable}
									disabled={false}
								/>
							</div>
						</>
					)}

					{/* Hors Alsace et Moselle  */}
					{formFields.gender === HORS_AM_GENDER && (
						<>
							<div className="lg:w-1/2 w-full p-5">
								{/* rna */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.rna}
									name="rna"
									type="text"
									maxLength="9"
									label={t("edit.rna", { ns: "partner" })}
									className="mb-5"
									required={true}
									customRules={[
										{
											key: "rna-invalid",
											check: (value: string) => {
												return (
													(value &&
														value.length > 1 &&
														isRnaValid(value)) ||
													true
												)
											},
											message: "Le numéro RNA n'est pas valide.",
										},
									]}
									readonly={!formEvents.editable}
								/>
							</div>
						</>
					)}

					{formFields.gender === AM_GENDER && ( // Gender 2
						<>
							<div className="w-full p-5">
								<h3>{t("edit.register_number", { ns: "partner" })}</h3>
							</div>

							<div className="lg:w-1/4 w-full p-5">
								{/* Date */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.registre_date}
									name="registre_date"
									type="date"
									label={t("edit.date", { ns: "partner" })}
									className="mb-5"
									required={true}
									disabled={!formEvents.editable}
								/>
							</div>

							<div className="lg:w-1/4 w-full p-5">
								{/* volume */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.registre_volume}
									name="registre_volume"
									type="text"
									label={t("edit.volume", { ns: "partner" })}
									className="mb-5"
									required={true}
									maxLength="3"
									readonly={!formEvents.editable}
								/>
							</div>

							<div className="lg:w-1/4 w-full p-5">
								{/* folio */}
								<FormElement
									{...formElementCommonProps}
									value={formFields.registre_folio}
									name="registre_folio"
									type="text"
									label={t("edit.folio", { ns: "partner" })}
									className="mb-5"
									required={true}
									maxLength="3"
									readonly={!formEvents.editable}
								/>
							</div>
						</>
					)}
				</div>

				{isEdit && !isViewMode && (
					<div className="flex justify-end">
						{formEvents.editable ? (
							<>
								<button
									type="button"
									className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5"
									onClick={toggleEditableForm}
								>
									{t("global.cancel")}
								</button>
								<button
									type="submit"
									className="c-btn c-btn--primary c-btn--m px-12 mx-5"
									onClick={handleSubmit}
									disabled={!formEvents.ready || !formHasChanged}
								>
									{t("global.save")}
								</button>
							</>
						) : (
							<button
								type="button"
								className="c-btn c-btn--primary px-12 mx-5"
								onClick={toggleEditableForm}
							>
								{t("global.update")}
							</button>
						)}
					</div>
				)}
			</section>
		</form>
	)
}
